/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useState } from 'react'

import { supabase } from '../supabaseClient.js'
import {
  fetchShopifyProductsByIds,
  normalizeShopifyProductId,
} from '../utils/shopify.js'

const WishlistContext = createContext(null)

function normalizeText(value) {
  return String(value ?? '').trim()
}

function getWishlistProductIdentifier(product) {
  return normalizeShopifyProductId(
    product?.id ?? product?.uuid ?? product?.product_id ?? '',
  )
}

function normalizeWishlistProduct(product, { createdAt = null, fallbackId = null } = {}) {
  if (!product || typeof product !== 'object') {
    return null
  }

  const resolvedId = normalizeText(
    normalizeShopifyProductId(
      product.id ?? product.uuid ?? product.product_id ?? fallbackId ?? '',
    ),
  )

  if (!resolvedId) {
    return null
  }

  const resolvedTitle = normalizeText(
    product.title ??
      product.name ??
      product.product_name ??
      product.product_title ??
      '',
  )

  return {
    ...product,
    id: resolvedId,
    uuid: product.uuid ?? resolvedId,
    title: product.title ?? resolvedTitle,
    name: product.name ?? resolvedTitle,
    product_name: product.product_name ?? resolvedTitle,
    description:
      product.description ?? product.summary ?? product.details ?? '',
    image_url:
      product.image_url ??
      product.featured_image ??
      product.featuredImage?.url ??
      product.image ??
      '',
    handle: product.handle ?? product.slug ?? '',
    base_price: product.base_price ?? product.price_inr ?? product.price ?? null,
    price_inr: product.price_inr ?? product.price ?? product.base_price ?? null,
    category: product.category ?? product.product_category ?? product.collection ?? '',
    product_category:
      product.product_category ?? product.category ?? product.collection ?? '',
    vibe_category: product.vibe_category ?? product.category ?? '',
    created_at: product.created_at ?? createdAt ?? null,
    wishlist_created_at: createdAt,
  }
}

export function WishlistProvider({ children }) {
  const [user, setUser] = useState(null)
  const [authReady, setAuthReady] = useState(false)
  const [wishlistLoading, setWishlistLoading] = useState(true)
  const [wishlistError, setWishlistError] = useState('')
  const [wishlistItems, setWishlistItems] = useState([])
  const [wishlistedProductIds, setWishlistedProductIds] = useState(
    () => new Set(),
  )
  const [wishlistBusyProductId, setWishlistBusyProductId] = useState('')

  useEffect(() => {
    let isActive = true

    async function loadSession() {
      try {
        if (!supabase?.auth) {
          if (isActive) {
            setUser(null)
          }
          return
        }

        const { data } = await supabase.auth.getSession()

        if (isActive) {
          setUser(data.session?.user ?? null)
        }
      } catch {
        if (isActive) {
          setUser(null)
        }
      } finally {
        if (isActive) {
          setAuthReady(true)
        }
      }
    }

    loadSession()

    if (!supabase?.auth) {
      return () => {
        isActive = false
      }
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (isActive) {
        setUser(session?.user ?? null)
      }
    })

    return () => {
      isActive = false
      subscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (!authReady) {
      return undefined
    }

    if (!user?.id) {
      setWishlistItems([])
      setWishlistedProductIds(new Set())
      setWishlistError('')
      setWishlistLoading(false)
      return undefined
    }

    let isActive = true
    setWishlistLoading(true)
    setWishlistError('')

    async function loadWishlist() {
      try {
        if (!supabase?.from) {
          throw new Error('Supabase is not available right now.')
        }

        const { data: wishlistData, error: wishlistFetchError } = await supabase
          .from('wishlists')
          .select('created_at, product_id')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })

        if (!isActive) {
          return
        }

        if (wishlistFetchError) {
          throw wishlistFetchError
        }

        const rows = Array.isArray(wishlistData) ? wishlistData : []
        const savedIds = rows
          .map((row) => normalizeShopifyProductId(row?.product_id))
          .filter(Boolean)
        const wishlistRowsById = new Map()

        rows.forEach((row) => {
          const productId = normalizeShopifyProductId(row?.product_id)

          if (!productId || wishlistRowsById.has(productId)) {
            return
          }

          wishlistRowsById.set(productId, row)
        })

        if (!savedIds.length) {
          if (isActive) {
            setWishlistItems([])
            setWishlistedProductIds(new Set())
          }

          return
        }

        const fetchedProducts = await fetchShopifyProductsByIds(savedIds)

        if (!isActive) {
          return
        }

        const productsById = new Map()

        fetchedProducts.forEach((product) => {
          const productId = normalizeShopifyProductId(product?.id)

          if (!productId || productsById.has(productId)) {
            return
          }

          productsById.set(productId, product)
        })

        const nextProducts = []
        const nextIds = new Set()

        savedIds.forEach((productId) => {
          const product = productsById.get(productId)

          if (!product) {
            return
          }

          const createdAt = wishlistRowsById.get(productId)?.created_at ?? null
          const normalizedProduct = normalizeWishlistProduct(product, {
            createdAt,
            fallbackId: productId,
          })

          if (!normalizedProduct) {
            return
          }

          const normalizedProductId = getWishlistProductIdentifier(
            normalizedProduct,
          )

          if (!normalizedProductId || nextIds.has(normalizedProductId)) {
            return
          }

          nextIds.add(normalizedProductId)
          nextProducts.push(normalizedProduct)
        })

        if (isActive) {
          setWishlistItems(nextProducts)
          setWishlistedProductIds(nextIds)
        }
      } catch (loadError) {
        if (isActive) {
          console.error('Failed to load wishlists.', loadError)
          setWishlistItems([])
          setWishlistedProductIds(new Set())
          setWishlistError('Unable to load your saved vibes right now.')
        }
      } finally {
        if (isActive) {
          setWishlistLoading(false)
        }
      }
    }

    loadWishlist()

    return () => {
      isActive = false
    }
  }, [authReady, user?.id])

  async function syncWishlistRecord(
    product,
    { currentUser = user, forceAdd = false } = {},
  ) {
    const userId = String(currentUser?.id || '').trim()
    const rawProductId = String(
      product?.id ?? product?.uuid ?? product?.product_id ?? '',
    ).trim()
    const productId = getWishlistProductIdentifier(product)

    if (!userId || !productId) {
      return null
    }

    if (!supabase?.from) {
      throw new Error('Supabase is not available right now.')
    }

    if (wishlistBusyProductId && wishlistBusyProductId !== productId) {
      return null
    }

    setWishlistBusyProductId(productId)

    try {
      const isWishlisted = wishlistedProductIds.has(productId)

      if (isWishlisted && !forceAdd) {
        const deleteProductIds = [productId, rawProductId]
          .map((value) => String(value || '').trim())
          .filter(Boolean)

        const { error } = await supabase
          .from('wishlists')
          .delete()
          .eq('user_id', userId)
          .in('product_id', deleteProductIds)

        if (error) {
          throw error
        }

        setWishlistedProductIds((current) => {
          const next = new Set(current)
          next.delete(productId)
          return next
        })

        setWishlistItems((current) =>
          current.filter(
            (item) => getWishlistProductIdentifier(item) !== productId,
          ),
        )

        return 'removed'
      }

      const { error } = await supabase.from('wishlists').insert({
        user_id: userId,
        product_id: productId,
      })

      if (error) {
        const errorMessage = String(error?.message || '').toLowerCase()
        const errorCode = String(error?.code || '')

        if (errorCode !== '23505' && !errorMessage.includes('duplicate')) {
          throw error
        }
      }

      const normalizedProduct = normalizeWishlistProduct(product, {
        createdAt: new Date().toISOString(),
        fallbackId: productId,
      })

      if (normalizedProduct) {
        setWishlistItems((current) => {
          const filtered = current.filter(
            (item) => getWishlistProductIdentifier(item) !== productId,
          )

          return [normalizedProduct, ...filtered]
        })
      }

      setWishlistedProductIds((current) => {
        const next = new Set(current)
        next.add(productId)
        return next
      })

      return 'added'
    } finally {
      setWishlistBusyProductId('')
    }
  }

  async function toggleWishlist(product, options = {}) {
    if (!user?.id) {
      return { status: 'unauthenticated' }
    }

    const result = await syncWishlistRecord(product, {
      currentUser: options.currentUser ?? user,
      forceAdd: Boolean(options.forceAdd),
    })

    return { status: result ?? 'skipped' }
  }

  return (
    <WishlistContext.Provider
      value={{
        authReady,
        user,
        wishlistLoading,
        wishlistError,
        wishlistItems,
        wishlistedProductIds,
        wishlistBusyProductId,
        syncWishlistRecord,
        toggleWishlist,
      }}
    >
      {children}
    </WishlistContext.Provider>
  )
}

export function useWishlist() {
  const context = useContext(WishlistContext)

  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider.')
  }

  return context
}
